// Starea globală: setări (sincronizate cu serverul) + datele de piață (live).

import { bus } from './util.js';
import { client } from './exchange-client.js';

const SAVE_DEBOUNCE = 600;
const AGGREGATES_INTERVAL = 5000;
const SPARKLINE_INTERVAL = 120_000;

/** Chei ținute doar local (preferințe de afișare) — nu au ce căuta pe server. */
const LOCAL_ONLY = new Set([
  'sx_grid_mode', 'sx_coins_sidebar_visible', 'sx_coins_sidebar_width',
  'sx_coins_list_view', 'sx_coins_col_widths', 'sx_techdata_compact',
  'sx_lw_tb_collapsed', 'sx_levels_hidden', 'sx_use_proxy', 'sx_cell_tfs',
  // Tema trebuie citită înainte de prima randare, iar răspunsul serverului vine prea
  // târziu pentru asta: interfața ar clipi o dată în paleta implicită.
  'sx_theme', 'sx_tema_aleasa', 'sx_candle_colors',
]);

class Settings {
  #values = {};
  #dirty = new Set();
  #timer = null;
  #paza = false;
  loaded = false;

  async load() {
    try {
      const resp = await fetch('/api/settings');
      this.#values = await resp.json();
    } catch (err) {
      console.warn('[settings] serverul nu răspunde, folosesc localStorage', err);
      this.#values = {};
    }
    for (const key of LOCAL_ONLY) {
      const raw = localStorage.getItem(key);
      if (raw !== null) {
        try { this.#values[key] = JSON.parse(raw); } catch { /* valoare coruptă */ }
      }
    }
    this.loaded = true;
    bus.emit('settings:loaded', this.#values);
    return this.#values;
  }

  get(key, fallback = null) {
    return this.#values[key] ?? fallback;
  }

  all() {
    return { ...this.#values };
  }

  set(key, value) {
    if (JSON.stringify(this.#values[key]) === JSON.stringify(value)) return;
    this.#values[key] = value;
    if (LOCAL_ONLY.has(key)) {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      this.#dirty.add(key);
      this.#scheduleSave();
    }
    bus.emit('settings:changed', { key, value });
    bus.emit(`settings:${key}`, value);
  }

  #scheduleSave() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.flush(), SAVE_DEBOUNCE);
    this.#pazaLaPlecare();
  }

  /**
   * Ce s-a schimbat in ultima clipa pleaca si daca fila se inchide.
   *
   * Intre schimbare si trimitere sunt sase sute de milisecunde de asteptare, ca sa nu
   * trimitem cate o cerere pentru fiecare tastare. Cine deseneaza o linie si inchide
   * indata fila pierdea insa tocmai linia aceea. `pagehide` e singurul eveniment pe care
   * telefoanele il dau de fiecare data — `beforeunload` e sarit cand treci in alta
   * aplicatie, iar `unload` nu mai vine deloc pe iOS.
   */
  #pazaLaPlecare() {
    if (this.#paza) return;
    this.#paza = true;
    const acum = () => {
      if (!this.#dirty.size) return;
      const patch = {};
      for (const key of this.#dirty) patch[key] = this.#values[key];
      this.#dirty.clear();
      clearTimeout(this.#timer);
      // `sendBeacon` pleaca si dupa ce pagina a murit; `fetch` obisnuit e taiat acolo.
      try {
        navigator.sendBeacon('/api/settings/save',
          new Blob([JSON.stringify(patch)], { type: 'application/json' }));
      } catch { /* daca nu se poate, macar n-am stricat nimic */ }
    };
    window.addEventListener('pagehide', acum);
    document.addEventListener('visibilitychange', () => { if (document.hidden) acum(); });
  }

  async flush() {
    if (!this.#dirty.size) return;
    const patch = {};
    for (const key of this.#dirty) patch[key] = this.#values[key];
    this.#dirty.clear();
    try {
      await fetch('/api/settings/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
    } catch (err) {
      console.warn('[settings] salvarea a eșuat', err);
      for (const key of Object.keys(patch)) this.#dirty.add(key);
    }
  }
}

class MarketData {
  /** symbol -> metrici (price, change24h, quoteVolume, volatility24h, trades, funding, corr24, volTrend) */
  rows = new Map();
  sparklines = new Map();
  candles = new Map();   // OHLC pentru cardurile radarului
  exchange = 'binance';
  market = 'futures';
  ts = 0;
  #unsubTicker = null;
  #timers = [];

  async start(exchange, market) {
    this.stop();
    this.exchange = exchange;
    this.market = market;
    await this.refreshAggregates();
    this.refreshSparklines();

    this.#timers.push(setInterval(() => this.refreshAggregates(), AGGREGATES_INTERVAL));
    this.#timers.push(setInterval(() => this.refreshSparklines(), SPARKLINE_INTERVAL));

    // Prețurile live vin din WS și suprascriu snapshot-ul între poll-uri.
    this.#unsubTicker = client.subscribeTickerAll(exchange, market, (tickers) => {
      for (const t of tickers) {
        const row = this.rows.get(t.symbol);
        if (!row) continue;
        row.price = t.price;
        row.change24h = t.change24h;
        if (t.quoteVolume) row.quoteVolume = t.quoteVolume;
        if (t.trades) row.trades = t.trades;
        if (t.high24h) row.high24h = t.high24h;
        if (t.low24h) row.low24h = t.low24h;
      }
      bus.emit('market:tick');
    });
  }

  stop() {
    this.#timers.forEach(clearInterval);
    this.#timers = [];
    this.#unsubTicker?.();
    this.#unsubTicker = null;
  }

  async refreshAggregates() {
    try {
      const resp = await fetch(`/api/aggregates?exchange=${this.exchange}&market=${this.market}`);
      const data = await resp.json();
      this.ts = data.ts;
      for (const [symbol, row] of Object.entries(data.data || {})) {
        const existing = this.rows.get(symbol);
        // Păstrăm prețul live din WS dacă snapshot-ul e mai vechi.
        this.rows.set(symbol, existing ? { ...row, price: existing.price ?? row.price } : row);
      }
      // Simbolurile dispărute (delistate) ies din listă.
      if (data.data) {
        for (const symbol of [...this.rows.keys()]) {
          if (!(symbol in data.data)) this.rows.delete(symbol);
        }
      }
      bus.emit('market:aggregates', this.rows);
    } catch (err) {
      console.warn('[market] agregatele nu s-au încărcat', err);
    }
  }

  async refreshSparklines() {
    try {
      const resp = await fetch(`/api/sparklines?exchange=${this.exchange}&market=${this.market}`);
      const data = await resp.json();
      this.sparklines = new Map(Object.entries(data.data || {}));
      this.candles = new Map(Object.entries(data.candles || {}));
      bus.emit('market:sparklines', this.sparklines);
    } catch { /* sparkline-urile sunt opționale */ }
  }

  list() {
    return [...this.rows.entries()].map(([symbol, row]) => ({ symbol, ...row }));
  }

  get(symbol) {
    return this.rows.get(symbol) || null;
  }

  /** Lumânări [o,h,l,c] pentru card. Serverul le trimite doar pentru monedele active. */
  candleSpark(symbol) {
    return this.candles.get(symbol) || null;
  }

  spark(symbol) {
    return this.sparklines.get(symbol) || null;
  }
}

export const settings = new Settings();
export const market = new MarketData();
